/**
 * Messages API - Mudo Network
 * 
 * Functions for encrypted in-app messaging between users
 */

import { supabase } from '../../supabaseClient';
import { apiFetch } from '../apiFetch';
import { getCachedUser } from '../../contexts/AuthContext';

// Simple encryption/decryption using base64 (for demo - use proper encryption in production)
const encryptMessage = (text: string): string => {
  try {
    return btoa(unescape(encodeURIComponent(text)));
  } catch {
    return text;
  }
};

const decryptMessage = (encrypted: string): string => {
  try {
    return decodeURIComponent(escape(atob(encrypted)));
  } catch {
    return encrypted;
  }
};

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  product_id?: string;
  workshop_id?: string;
  encrypted_content: string;
  subject?: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

export interface DecryptedMessage extends Omit<Message, 'encrypted_content'> {
  content: string;
}

/**
 * Send a message
 */
export async function sendMessage({
  recipientId,
  content,
  subject,
  productId,
  workshopId,
}: {
  recipientId: string;
  content: string;
  subject?: string;
  productId?: string;
  workshopId?: string;
}) {
  try {
    const encrypted = encryptMessage(content);
    
    // Get current user ID from centralized auth state (no direct supabase.auth call)
    const user = getCachedUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        recipient_id: recipientId,
        encrypted_content: encrypted,
        subject: subject || null,
        product_id: productId || null,
        workshop_id: workshopId || null,
      })
      .select()
      .single();

    if (error) throw error;

    // Normalize to include decrypted content for UI consumers
    const normalized = {
      ...data,
      content: decryptMessage(data.encrypted_content),
    } as any;

    try {
      await apiFetch('/api/messages/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipientId,
          content,
          threadId: [user.id, recipientId].sort().join('_'),
        }),
      });
    } catch (notifyError) {
      console.warn('[messagesApi] notify failed', notifyError);
    }

    return { data: normalized, error: null };
  } catch (error: any) {
    try {
      console.error('Error sending message:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code,
      });
    } catch {}
    return { data: null, error };
  }
}

/**
 * Get inbox messages (received)
 */
export async function getInboxMessages(limit: number = 50): Promise<{ data: DecryptedMessage[] | null; error: any }> {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const currentUser = getCachedUser();
    if (!currentUser?.id) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('recipient_id', currentUser.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Decrypt messages
    const decrypted: DecryptedMessage[] = (data || []).map((msg: Message) => ({
      ...msg,
      content: decryptMessage(msg.encrypted_content),
    }));

    return { data: decrypted, error: null };
  } catch (error) {
    console.error('Error fetching inbox:', error);
    return { data: null, error };
  }
}

/**
 * Get sent messages
 */
export async function getSentMessages(limit: number = 50): Promise<{ data: DecryptedMessage[] | null; error: any }> {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const currentUser = getCachedUser();
    if (!currentUser?.id) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('sender_id', currentUser.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Decrypt messages
    const decrypted: DecryptedMessage[] = (data || []).map((msg: Message) => ({
      ...msg,
      content: decryptMessage(msg.encrypted_content),
    }));

    return { data: decrypted, error: null };
  } catch (error) {
    console.error('Error fetching sent messages:', error);
    return { data: null, error };
  }
}

/**
 * Mark message as read
 */
export async function markAsRead(messageId: string) {
  try {
    const { data, error } = await supabase
      .from('messages')
      .update({
        is_read: true,
        read_at: new Date().toISOString(),
      })
      .eq('id', messageId)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error marking message as read:', error);
    return { data: null, error };
  }
}

/**
 * Delete a message
 */
export async function deleteMessage(messageId: string) {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Error deleting message:', error);
    return { data: false, error };
  }
}

/**
 * Delete all messages in a thread (conversation with another user)
 */
export async function deleteThread(otherUserId: string) {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const currentUser = getCachedUser();
    if (!currentUser?.id) {
      throw new Error('Not authenticated');
    }

    // Delete all messages in both directions (sent and received)
    const { error } = await supabase
      .from('messages')
      .delete()
      .or(`and(sender_id.eq.${currentUser.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${currentUser.id})`);

    if (error) throw error;

    return { data: true, error: null };
  } catch (error) {
    console.error('Error deleting thread:', error);
    return { data: false, error };
  }
}


/**
 * Get unread message count
 */
export async function getUnreadCount() {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const currentUser = getCachedUser();
    if (!currentUser?.id) {
      return { data: 0, error: null };
    }

    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', currentUser.id)
      .eq('is_read', false);

    if (error) throw error;

    return { data: count || 0, error: null };
  } catch (error) {
    console.error('Error getting unread count:', error);
    return { data: 0, error };
  }
}

/**
 * Get conversation between two users
 */
export async function getConversation(otherUserId: string, limit: number = 50): Promise<{ data: DecryptedMessage[] | null; error: any }> {
  try {
    // Get current user from centralized auth state (no direct supabase.auth call)
    const currentUser = getCachedUser();
    if (!currentUser?.id) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${currentUser.id},recipient_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},recipient_id.eq.${currentUser.id})`)
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) throw error;

    // Decrypt messages
    const decrypted: DecryptedMessage[] = (data || []).map((msg: Message) => ({
      ...msg,
      content: decryptMessage(msg.encrypted_content),
    }));

    return { data: decrypted, error: null };
  } catch (error) {
    console.error('Error fetching conversation:', error);
    return { data: null, error };
  }
}

/**
 * Fetch seller offer inbox (shop_offers joined to shop_listings) for listings owned by sellerId.
 */
export async function fetchSellerOfferInbox(sellerId: string) {
  // For now, show all offers (avoid schema filter issues); caller can filter client-side if needed.
  const { data, error } = await supabase
    .from("shop_offers")
    .select(
      `
      id,
      listing_id,
      buyer_id,
      amount,
      amount_cents,
      currency,
      status,
      message,
      buyer_name,
      buyer_email,
      created_at,
      listing:shop_listings (
        id,
        title,
        price,
        main_image_url,
        thumb_url,
        owner,
        studio_id,
        seller_id,
        created_by,
        createdBy
      )
    `
    )
    .order("created_at", { ascending: false });

  return { data, error };
}
